import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import NavBar from "@/components/navigation/NavBar";
import Footer from "@/components/navigation/Footer";
import { sanityApi, useSanityQuery } from "@/features/sanity";
import { CityPage as CityPageType } from "./types";
import BlockRenderer from "./components/BlockRenderer";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeftIcon } from "@radix-ui/react-icons";

/**
 * Loading skeleton for city page
 */
const CityPageSkeleton = () => (
  <div className="bg-prime-50 min-h-screen">
    <NavBar className="relative bg-white/100" />
    <div className="logo-loader">
      <img
        src="/jakobs_logo.png"
        alt="Helga Jakobs Immobilien Logo"
        className="logo"
      />
    </div>
    <Footer />
  </div>
);

/**
 * Error component for city page
 */
const CityPageError = ({
  error,
  navigate,
}: {
  error: string;
  navigate: (path: string) => void;
}) => (
  <div className="bg-prime-50 min-h-screen">
    <NavBar className="relative bg-white/100" />
    <div className="container mx-auto text-center py-20 px-4">
      <h1 className="text-4xl font-bold text-prime-900 mb-4">
        Seite nicht gefunden
      </h1>
      <p className="text-red-600 text-xl mb-8">
        {error || "Diese Stadt-Seite konnte nicht gefunden werden."}
      </p>
      <Button
        onClick={() => navigate("/")}
        className="inline-flex items-center gap-2"
        variant="default"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Zurück zur Startseite
      </Button>
    </div>
    <Footer />
  </div>
);

/**
 * City Page Component
 * Displays dynamic city pages managed through Sanity CMS using the Page Builder pattern
 */
export default function CityPage() {
  const { citySlug } = useParams<{ citySlug: string }>();
  const navigate = useNavigate();

  const {
    data: cityPage,
    loading,
    error,
  } = useSanityQuery<CityPageType | null>(async () => {
    if (!citySlug) {
      throw new Error("Ungültige Stadt-URL");
    }
    const data = await sanityApi.fetchCityPageBySlug(citySlug);
    if (!data) {
      throw new Error(`Die Seite für "${citySlug}" wurde nicht gefunden.`);
    }
    return data;
  }, [citySlug]);

  // Show loading state
  if (loading) {
    return <CityPageSkeleton />;
  }

  // Show error state
  if (error || !cityPage) {
    return (
      <CityPageError
        error={error?.message || "Seite nicht gefunden"}
        navigate={navigate}
      />
    );
  }

  // Prepare SEO data
  const metaTitle =
    cityPage.seo?.metaTitle || `${cityPage.title} | Helga Jakobs Immobilien`;
  const metaDescription = cityPage.seo?.metaDescription || cityPage.description;
  const heroImageUrl = cityPage.heroImage?.asset?.url;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {cityPage.seo?.keywords && cityPage.seo.keywords.length > 0 && (
          <meta name="keywords" content={cityPage.seo.keywords.join(", ")} />
        )}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        {heroImageUrl && <meta property="og:image" content={heroImageUrl} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {heroImageUrl && <meta name="twitter:image" content={heroImageUrl} />}
      </Helmet>

      <div className="bg-white min-h-screen flex flex-col">
        <NavBar className="relative bg-white/100" />

        <main className="flex-grow">
          <BlockRenderer blocks={cityPage.pageBuilder} />
        </main>

        <Footer />
      </div>
    </>
  );
}
